import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  useColorScheme,
  Dimensions,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { PieChart } from 'react-native-chart-kit';

const screenWidth = Dimensions.get('window').width;

const NutrientDistributionScreen = ({ navigation }) => {
  const isDarkMode = useColorScheme() === 'dark';
  const [activeTab, setActiveTab] = useState('macronutrients');
  
  // 模拟数据 - 在实际项目中应从API或本地存储获取
  const [nutritionData, setNutritionData] = useState({
    // 实际摄入的宏量营养素
    actual: {
      protein: 85,  // 蛋白质(克)
      carbs: 220,   // 碳水化合物(克)
      fat: 60,      // 脂肪(克)
    },
    // 推荐的宏量营养素
    recommended: {
      protein: 100,  // 蛋白质(克)
      carbs: 250,    // 碳水化合物(克)
      fat: 70,       // 脂肪(克)
    },
    // 微量营养素
    micronutrients: {
      vitamins: {
        A: { actual: 800, recommended: 900, unit: 'μg' },
        C: { actual: 75, recommended: 90, unit: 'mg' },
        D: { actual: 15, recommended: 20, unit: 'μg' },
        E: { actual: 12, recommended: 15, unit: 'mg' },
        K: { actual: 80, recommended: 120, unit: 'μg' },
        B1: { actual: 1.0, recommended: 1.2, unit: 'mg' },
        B2: { actual: 1.1, recommended: 1.3, unit: 'mg' },
        B3: { actual: 14, recommended: 16, unit: 'mg' },
        B6: { actual: 1.5, recommended: 1.7, unit: 'mg' },
        B12: { actual: 2.0, recommended: 2.4, unit: 'μg' },
        Folate: { actual: 320, recommended: 400, unit: 'μg' },
      },
      minerals: {
        Calcium: { actual: 950, recommended: 1000, unit: 'mg' },
        Iron: { actual: 12, recommended: 18, unit: 'mg' },
        Magnesium: { actual: 320, recommended: 400, unit: 'mg' },
        Phosphorus: { actual: 700, recommended: 700, unit: 'mg' },
        Potassium: { actual: 3200, recommended: 4700, unit: 'mg' },
        Sodium: { actual: 2300, recommended: 2300, unit: 'mg' },
        Zinc: { actual: 10, recommended: 11, unit: 'mg' },
        Copper: { actual: 0.8, recommended: 0.9, unit: 'mg' },
        Manganese: { actual: 1.8, recommended: 2.3, unit: 'mg' },
        Selenium: { actual: 50, recommended: 55, unit: 'μg' },
      }
    }
  });
  
  // 计算宏量营养素提供的卡路里
  const calculateCalories = () => {
    const { protein, carbs, fat } = nutritionData.actual;
    return {
      protein: protein * 4,  // 蛋白质：4卡/克
      carbs: carbs * 4,      // 碳水：4卡/克
      fat: fat * 9,          // 脂肪：9卡/克
    };
  };
  
  // 计算宏量营养素占比
  const calculateMacroPercentages = () => {
    const calories = calculateCalories();
    const totalCalories = calories.protein + calories.carbs + calories.fat;
    
    return {
      protein: calories.protein / totalCalories,
      carbs: calories.carbs / totalCalories,
      fat: calories.fat / totalCalories,
    };
  };
  
  // 计算推荐宏量营养素占比
  const calculateRecommendedPercentages = () => {
    const { protein, carbs, fat } = nutritionData.recommended;
    const totalCalories = protein * 4 + carbs * 4 + fat * 9;
    
    return {
      protein: (protein * 4) / totalCalories,
      carbs: (carbs * 4) / totalCalories,
      fat: (fat * 9) / totalCalories,
    };
  };
  
  // 计算营养均衡度评分
  const calculateBalanceScore = () => {
    const actual = calculateMacroPercentages();
    const recommended = calculateRecommendedPercentages();
    
    // 计算实际与推荐的差异，差距越小分数越高
    const proteinDiff = Math.abs(actual.protein - recommended.protein);
    const carbsDiff = Math.abs(actual.carbs - recommended.carbs);
    const fatDiff = Math.abs(actual.fat - recommended.fat);
    
    // 总偏差，最大可能偏差为2(完全不符合推荐)
    const totalDeviation = proteinDiff + carbsDiff + fatDiff;
    // 将偏差转换为0-100的评分，偏差越小分数越高
    const score = Math.round((1 - totalDeviation / 2) * 100);
    
    return Math.max(0, Math.min(100, score)); // 确保分数在0-100之间
  };
  
  // 为饼图准备数据
  const getMacroChartData = () => {
    const percentages = calculateMacroPercentages();
    
    return [
      {
        name: '蛋白质',
        percentage: Math.round(percentages.protein * 100),
        color: '#10b981',
        legendFontColor: isDarkMode ? '#e0e0e0' : '#1f2937',
        legendFontSize: 12,
      },
      {
        name: '碳水',
        percentage: Math.round(percentages.carbs * 100),
        color: '#f59e0b',
        legendFontColor: isDarkMode ? '#e0e0e0' : '#1f2937',
        legendFontSize: 12,
      },
      {
        name: '脂肪',
        percentage: Math.round(percentages.fat * 100),
        color: '#ec4899',
        legendFontColor: isDarkMode ? '#e0e0e0' : '#1f2937',
        legendFontSize: 12,
      }
    ];
  };
  
  // 计算维生素摄入热力图的颜色
  const getVitaminHeatmapColor = (actual, recommended) => {
    const percentage = actual / recommended;
    
    if (percentage < 0.5) return '#ef4444'; // 严重不足 - 红色
    if (percentage < 0.8) return '#f97316'; // 不足 - 橙色
    if (percentage <= 1.2) return '#10b981'; // 适量 - 绿色
    if (percentage <= 1.5) return '#3b82f6'; // 充足 - 蓝色
    return '#8b5cf6'; // 过量 - 紫色
  };
  
  // 渲染维生素热力图单元格
  const renderVitaminCell = (name, data) => {
    const percentage = data.actual / data.recommended;
    const color = getVitaminHeatmapColor(data.actual, data.recommended);
    
    return (
      <View
        key={name}
        style={{
          width: '23%',
          aspectRatio: 1,
          margin: '1%',
          padding: 8,
          backgroundColor: color,
          borderRadius: 8,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Text style={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: 14,
          marginBottom: 4,
        }}>
          {name}
        </Text>
        <Text style={{
          color: 'white',
          fontSize: 12,
        }}>
          {Math.round(percentage * 100)}%
        </Text>
      </View>
    );
  };
  
  // 营养均衡度评分
  const balanceScore = calculateBalanceScore();
  
  // 评估营养均衡度
  const getBalanceScoreDescription = (score) => {
    if (score >= 90) return { text: '优秀', color: '#10b981' };
    if (score >= 75) return { text: '良好', color: '#3b82f6' };
    if (score >= 60) return { text: '一般', color: '#f59e0b' };
    return { text: '需改善', color: '#ef4444' };
  };
  
  const scoreDescription = getBalanceScoreDescription(balanceScore);
  
  return (
    <ScrollView 
      style={{
        flex: 1,
        backgroundColor: isDarkMode ? '#8fa0c1' : '#f9fafb',
      }}
      contentContainerStyle={{ padding: 16 }}
    >
      {/* 页面导航标签 */}
      <View style={{
        flexDirection: 'row',
        marginBottom: 16,
        backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
        elevation: 2,
      }}>
        <TouchableOpacity
          style={{
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            borderBottomWidth: activeTab === 'macronutrients' ? 2 : 0,
            borderBottomColor: '#2563eb',
          }}
          onPress={() => setActiveTab('macronutrients')}
        >
          <Text style={{
            fontWeight: activeTab === 'macronutrients' ? 'bold' : 'normal',
            color: activeTab === 'macronutrients' ? '#2563eb' : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            主要营养素
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={{
            flex: 1,
            paddingVertical: 12,
            alignItems: 'center',
            borderBottomWidth: activeTab === 'micronutrients' ? 2 : 0,
            borderBottomColor: '#2563eb',
          }}
          onPress={() => setActiveTab('micronutrients')}
        >
          <Text style={{
            fontWeight: activeTab === 'micronutrients' ? 'bold' : 'normal',
            color: activeTab === 'micronutrients' ? '#2563eb' : (isDarkMode ? '#e0e0e0' : '#6b7280'),
          }}>
            微量营养素
          </Text>
        </TouchableOpacity>
      </View>
      
      {activeTab === 'macronutrients' ? (
        <>
          {/* 主要营养素占比 */}
          <View style={{
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              主要营养素占比
            </Text>
            
            {/* 三大营养素配比饼图 */}
            <View style={{
              alignItems: 'center',
              marginBottom: 20,
            }}>
              <PieChart
                data={getMacroChartData()}
                width={screenWidth - 32}
                height={200}
                chartConfig={{
                  color: (opacity = 1) => isDarkMode ? `rgba(255, 255, 255, ${opacity})` : `rgba(0, 0, 0, ${opacity})`,
                }}
                accessor="percentage"
                backgroundColor={isDarkMode ? 'transparent' : 'transparent'}
                paddingLeft="15"
                absolute
              />
            </View>
            
            {/* 与推荐配比差异对比 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 12,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              与推荐配比差异对比
            </Text>
            
            <View style={{
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
            }}>
              {/* 蛋白质对比 */}
              <View style={{ marginBottom: 12 }}>
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}>
                  <Text style={{
                    fontSize: 14,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    蛋白质
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    {Math.round(calculateMacroPercentages().protein * 100)}% vs {Math.round(calculateRecommendedPercentages().protein * 100)}%
                  </Text>
                </View>
                <View style={{
                  flexDirection: 'row',
                  height: 12,
                  backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}>
                  <View style={{
                    width: `${Math.round(calculateMacroPercentages().protein * 100)}%`,
                    backgroundColor: '#10b981',
                  }} />
                </View>
                <View style={{
                  position: 'absolute',
                  top: 20,
                  left: `${Math.round(calculateRecommendedPercentages().protein * 100)}%`,
                  width: 2,
                  height: 12,
                  backgroundColor: 'white',
                  marginLeft: -1,
                }} />
              </View>
              
              {/* 碳水对比 */}
              <View style={{ marginBottom: 12 }}>
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}>
                  <Text style={{
                    fontSize: 14,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    碳水化合物
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    {Math.round(calculateMacroPercentages().carbs * 100)}% vs {Math.round(calculateRecommendedPercentages().carbs * 100)}%
                  </Text>
                </View>
                <View style={{
                  flexDirection: 'row',
                  height: 12,
                  backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}>
                  <View style={{
                    width: `${Math.round(calculateMacroPercentages().carbs * 100)}%`,
                    backgroundColor: '#f59e0b',
                  }} />
                </View>
                <View style={{
                  position: 'absolute',
                  top: 20,
                  left: `${Math.round(calculateRecommendedPercentages().carbs * 100)}%`,
                  width: 2,
                  height: 12,
                  backgroundColor: 'white',
                  marginLeft: -1,
                }} />
              </View>
              
              {/* 脂肪对比 */}
              <View>
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}>
                  <Text style={{
                    fontSize: 14,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    脂肪
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  }}>
                    {Math.round(calculateMacroPercentages().fat * 100)}% vs {Math.round(calculateRecommendedPercentages().fat * 100)}%
                  </Text>
                </View>
                <View style={{
                  flexDirection: 'row',
                  height: 12,
                  backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}>
                  <View style={{
                    width: `${Math.round(calculateMacroPercentages().fat * 100)}%`,
                    backgroundColor: '#ec4899',
                  }} />
                </View>
                <View style={{
                  position: 'absolute',
                  top: 20,
                  left: `${Math.round(calculateRecommendedPercentages().fat * 100)}%`,
                  width: 2,
                  height: 12,
                  backgroundColor: 'white',
                  marginLeft: -1,
                }} />
              </View>
            </View>
            
            {/* 营养均衡度评分 */}
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              marginBottom: 12,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              营养均衡度评分
            </Text>
            
            <View style={{
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <View style={{ flex: 1 }}>
                <Text style={{
                  fontSize: 14,
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                  marginBottom: 4,
                }}>
                  均衡度评分
                </Text>
                <Text style={{
                  fontSize: 28,
                  fontWeight: 'bold',
                  color: scoreDescription.color,
                }}>
                  {balanceScore}
                </Text>
              </View>
              
              <View style={{
                backgroundColor: isDarkMode ? '#718096' : 'white',
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 16,
              }}>
                <Text style={{
                  fontSize: 16,
                  fontWeight: 'bold',
                  color: scoreDescription.color,
                }}>
                  {scoreDescription.text}
                </Text>
              </View>
            </View>
          </View>
        </>
      ) : (
        <>
          {/* 微量营养素追踪 */}
          <View style={{
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
            backgroundColor: isDarkMode ? '#a9b6d0' : '#ffffff',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 2,
          }}>
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              维生素摄入热力图
            </Text>
            
            {/* 维生素摄入热力图 */}
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              marginBottom: 20,
            }}>
              {Object.entries(nutritionData.micronutrients.vitamins).map(([name, data]) => 
                renderVitaminCell(name, data)
              )}
            </View>
            
            {/* 热力图图例 */}
            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 24,
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 12,
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{
                  width: 16,
                  height: 16,
                  backgroundColor: '#ef4444',
                  borderRadius: 4,
                  marginRight: 4,
                }} />
                <Text style={{
                  fontSize: 12,
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                }}>
                  &lt;50%
                </Text>
              </View>
              
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{
                  width: 16,
                  height: 16,
                  backgroundColor: '#f97316',
                  borderRadius: 4,
                  marginRight: 4,
                }} />
                <Text style={{
                  fontSize: 12,
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                }}>
                  50-80%
                </Text>
              </View>
              
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{
                  width: 16,
                  height: 16,
                  backgroundColor: '#10b981',
                  borderRadius: 4,
                  marginRight: 4,
                }} />
                <Text style={{
                  fontSize: 12,
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                }}>
                  80-120%
                </Text>
              </View>
              
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{
                  width: 16,
                  height: 16,
                  backgroundColor: '#3b82f6',
                  borderRadius: 4,
                  marginRight: 4,
                }} />
                <Text style={{
                  fontSize: 12,
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                }}>
                  120-150%
                </Text>
              </View>
              
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={{
                  width: 16,
                  height: 16,
                  backgroundColor: '#8b5cf6',
                  borderRadius: 4,
                  marginRight: 4,
                }} />
                <Text style={{
                  fontSize: 12,
                  color: isDarkMode ? '#e0e0e0' : '#4b5563',
                }}>
                  &gt;150%
                </Text>
              </View>
            </View>
            
            <Text style={{
              fontSize: 18,
              fontWeight: 'bold',
              marginBottom: 16,
              color: isDarkMode ? 'white' : '#1f2937',
            }}>
              矿物质摄入进度条组
            </Text>
            
            {/* 矿物质摄入进度条组 */}
            <View style={{
              backgroundColor: isDarkMode ? '#8295b7' : '#f3f4f6',
              borderRadius: 8,
              padding: 16,
            }}>
              {Object.entries(nutritionData.micronutrients.minerals).map(([name, data]) => (
                <View key={name} style={{ marginBottom: 12 }}>
                  <View style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}>
                    <Text style={{
                      fontSize: 14,
                      color: isDarkMode ? '#e0e0e0' : '#4b5563',
                    }}>
                      {name}
                    </Text>
                    <Text style={{
                      fontSize: 14,
                      color: isDarkMode ? '#e0e0e0' : '#4b5563',
                    }}>
                      {data.actual} / {data.recommended} {data.unit}
                    </Text>
                  </View>
                  <View style={{
                    height: 8,
                    backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}>
                    <View style={{
                      width: `${Math.min(data.actual / data.recommended * 100, 100)}%`,
                      height: '100%',
                      backgroundColor: getVitaminHeatmapColor(data.actual, data.recommended),
                      borderRadius: 4,
                    }} />
                  </View>
                </View>
              ))}
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
};

export default NutrientDistributionScreen; 